import React, { useState, useEffect, useRef, useContext, useMemo, memo, useCallback } from 'react';
import { 
  Input, 
  Divider, 
  Spin, 
  Empty, 
  Typography,
  Tabs,
  Button,
  Space,
  Affix,
  message,
  BackTop,
  Tooltip
} from 'antd';
import { 
  SearchOutlined, 
  FileTextOutlined,
  AppstoreOutlined,
  FireOutlined,
  UpOutlined,
  PlusCircleFilled,
  MinusCircleFilled,
  StarOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import { listTests } from '../../services/test.service';
import { listPanels } from '../../services/panel.service';
import { PanelCode, CollectionMethod, ReqCode } from '../../enums/index.enum';
import { TextHelper } from '../../helpers/text.helper';
import PatientHelper from '../../helpers/patient.helper';
import { useSearchParams } from 'react-router-dom';
import Item from 'antd/es/list/Item';
import TestCard from './testCard.component';
import Cart from '../cart/cart.component';
import { Breakpoint } from '../../enums/index.enum';
import { UserContext } from '../../contexts/user.context';
import PosthogHelper from '../../helpers/posthog.helper';
import { EventCode, EventType } from '../../enums/index.enum';
import { addEvent } from '../../services/event.service';
import { debounce } from 'lodash';
import { List as VirtualList, AutoSizer } from 'react-virtualized';
import { listOrders } from '../../services/order.service';
import './testIndex.scss';

const { Title, Text } = Typography;
const PANEL_BLACKLIST = [PanelCode.ATHLETE];

// Increase debounce time for better performance
const debouncedSearch = debounce((value, callback) => {
  callback(value);
}, 200);

const SearchInput = ({ value, setSearchTerm }) => {
  const inputRef = useRef(null);
  const cursorPositionRef = useRef(null);
  const [localValue, setLocalValue] = useState(value);
  
  // Handle change with proper cursor management
  const handleSearchChange = (e) => {
    const newValue = e.target.value;
    cursorPositionRef.current = e.target.selectionStart;
  
    // Update local value immediately for responsive UI
    setLocalValue(newValue);
    
    // Debounce the actual search
    debouncedSearch(newValue, (debouncedValue) => {    
      setSearchTerm(debouncedValue);    
      
      // log search term - no need to await let finishing in background
      try {

        if (debouncedValue) {
        addEvent({
          eventType: EventType.SEARCH,
            meta: {
              searchTerm: debouncedValue
            }
          });
        }
      } catch (error) {
        console.error('Failed to log search term:', error);
      }
    });


  };

  // Sync local value when parent value changes
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  // Use effect to maintain focus and cursor position after value changes
  useEffect(() => {
    if (inputRef.current?.input) {
      const input = inputRef.current.input;
      const cursorPosition = cursorPositionRef.current;
      
      // Restore focus and cursor position
      input.focus();
      if (cursorPosition !== null) {
        input.setSelectionRange(cursorPosition, cursorPosition);
      }
    }
  }, [localValue]); // Run when search value changes
  
  
  return (
    <Input
      ref={inputRef}
      className="search-input"
      placeholder="Search by test name ..."
      prefix={<SearchOutlined />}
      onChange={handleSearchChange}
      value={localValue} // Use local value for immediate feedback
      size="large"
    />
  );
};

const MemoizedTestCard = memo(TestCard, (prevProps, nextProps) => {
  // Custom comparison function to prevent unnecessary re-renders
  // Only re-render if these specific props change
  return (
    prevProps.item._id === nextProps.item._id &&
    prevProps.isInCart === nextProps.isInCart &&
    prevProps.isInCartPanel === nextProps.isInCartPanel &&
    prevProps.isPanel === nextProps.isPanel &&
    prevProps.addToPanel === nextProps.addToPanel &&
    prevProps.removeFromPanel === nextProps.removeFromPanel
  );
});

const VirtualizedGrid = memo(({ items, renderItem, isMobileView }) => {
  // Dynamic column count based on screen width
  const getColumnCount = (width) => {
    if (width <= 576) return 1;      // xs screens
    if (width <= 768) return 2;      // sm screens
    if (width <= 992) return 3;      // md screens
    return 4;                        // lg and above screens
  };

  const rowHeight = 285; //isMobileView ? 290 : 300; // Adjust based on your card height
  
  return (
    <div style={{ minHeight: '100px', width: '100%' }}>
      <AutoSizer disableHeight>
        {({ width }) => {
          const columnCount = getColumnCount(width);
          const rowCount = Math.ceil(items.length / columnCount);
          const totalHeight = rowCount * rowHeight;
          
          return (
            <VirtualList
              width={width}
              height={totalHeight}
              rowCount={rowCount}
              rowHeight={rowHeight}
              overscanRowCount={2}
              rowRenderer={({ index, style }) => {
                const fromIndex = index * columnCount;
                const toIndex = Math.min(fromIndex + columnCount, items.length);
                
                return (
                  <div style={{ 
                    ...style, 
                    display: 'flex',
                    width: '100%',
                    gap: '16px', // Use gap for even spacing
                    // padding: '0 16px', // Add padding to container instead
                  }}>                    {items.slice(fromIndex, toIndex).map(item => (
                    <div 
                      key={item._id} 
                      style={{ 
                        width: `calc(${100 / columnCount}% - ${16 * (columnCount - 1) / columnCount}px)`, // Account for gaps
                        paddingBottom: '16px',
                      }}
                    >
                        {renderItem(item)}
                      </div>
                    ))}
                  </div>
                );
              }}
            />
          );
        }}
      </AutoSizer>
    </div>
  );
});


export const TestIndex = () => {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [categories, setCategories] = useState([]);
  const [activeTab, setActiveTab] = useState('all');
  const [searchParams, setSearchParams] = useSearchParams();
  const [cartOpen, setCartOpen] = useState(false);
  const [selectedTests, setSelectedTests] = useState([]);
  const [isMobileView, setIsMobileView] = useState(window.innerWidth <= Breakpoint.SM);
  const { currentUser } = useContext(UserContext);
  const [pastTests, setPastTests] = useState([]);
  const scrollPositionRef = useRef(0);

  useEffect(() => {
    const trackPosthog = () => {
      if (currentUser) {
        PosthogHelper.track(currentUser, EventCode.TEST_INDEX_VIEW);
      }
    };
    
    if (currentUser) {
      trackPosthog();
    }
  }, [currentUser]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        const [testsData, panelsData] = await Promise.all([
          listTests({ 
            populate: ['absoluteCategory'],
            filter: {
              name: {$exists: true},
              collectionMethods: { $in: [CollectionMethod.BLOOD] }
          },
          }),
          listPanels({
            filter: {
              active: true, 
              code: {$nin: PANEL_BLACKLIST},
              collectionMethods: { $in: [CollectionMethod.BLOOD] }
            },
            populate: ['productType', {path: 'tests', select: '_id code cost tagline name shortName longName collectionMethods', populate: {path: 'absoluteCategory'}}]
          })
        ]);
        
        // Filter out invalid panels
        const validPanels = panelsData.filter(panel => {
          // Check if requirement
          if (panel.reqCodes.includes(ReqCode.FEMALE_ONLY) && currentUser?.gender && !PatientHelper.isFemale(currentUser)) return false;
          if (panel.reqCodes.includes(ReqCode.MALE_ONLY) && currentUser?.gender && !PatientHelper.isMale(currentUser)) return false;

          // Check if the panel has a cost > 0, it's valid
          if (panel.cost > 0 || panel.productType) return true;    
          
          // Check if the panel has a productType, it's valid
          if (panel.productType) return true;

          // If the panel has no cost or productType, it's invalid
          return false;
        });

        // Filter out invalid tests
        const validTests = testsData.filter(test => { 

          // Check if requirement
          if (test.reqCodes.includes(ReqCode.FEMALE_ONLY) && currentUser?.gender && !PatientHelper.isFemale(currentUser)) return false;
          if (test.reqCodes.includes(ReqCode.MALE_ONLY) && currentUser?.gender && !PatientHelper.isMale(currentUser)) return false;

          // Check if the test has a cost > 0, it's valid
          if (test.cost > 0) return true;
          
          // Second check: if the test is included in any valid panel
          // return panelsData.some(panel => {
          //   // Find if the test exists in this panel
          //   const testInPanel = panel.tests.some(t => t._id === test._id);
          //   return testInPanel && (panel.productType || panel.cost);
          // });
          return false;
        });
        
        // Process only the valid panels
        const processedPanels = validPanels.map(panel => {
          // Extract all unique categories from the panel's tests
          const panelCategories = [];
          if (panel.tests && panel.tests.length > 0) {
            panel.tests.forEach(test => {
              if (test.absoluteCategory && !panelCategories.some(cat => cat._id === test.absoluteCategory._id)) {
                panelCategories.push(test.absoluteCategory);
              }
            });
          }
          
          return {
            ...panel,
            itemType: 'panel',
            shortName: panel.tests?.length === 1 ? panel.tests[0].shortName || panel.tests[0].name : panel.shortName || panel.name,
            name: panel.tests?.length === 1 ? panel.tests[0].name : panel.name,
            longName: panel.tests?.length === 1 ? panel.tests[0].name || panel.tests[0].longName : panel.name,
            tagline: panel.tests?.length === 1 ? panel.tests[0].tagline : panel.tagline,
            productType: panel.productType,
            testCategories: panelCategories,
            cost: panel.productType ? panel.productType.costIsServiceFee ? null : panel.productType.cost : panel.cost            
          };
        });


        const processedTests = validTests.map(test => ({
          ...test,
          itemType: 'test',
          cost: (() => {
            // First, check if the test has its own cost
            if (test.cost > 0) return test.cost;
            
            // // Second, find all panels containing this test that have a valid cost
            // const relevantPanelCosts = processedPanels
            //   .filter(panel => 
            //     panel.tests.some(t => t._id === test._id) && 
            //     panel.cost && 
            //     panel.cost > 0
            //   )
            //   .map(panel => panel.cost);
            
            // // If we found panels with costs, return the minimum cost
            // if (relevantPanelCosts.length > 0) {
            //   return Math.min(...relevantPanelCosts);
            // }
            
            // No valid cost found
            return 0; // or any default you prefer
          })(),
          productType: test.cost > 0 ? null : processedPanels.find(panel => panel.tests.some(t => t._id === test._id))?.productType
        }));

        const allItems = [...processedTests, ...processedPanels.filter(panel => panel.tests.length > 1)].sort((a, b) => 
          (a.shortName || a.name)?.localeCompare(b.shortName || b.name)
        );

        // Extract all unique categories for the filter dropdown
        const categoriesMap = {};
        
        // Add categories from individual tests
        allItems.forEach(item => {
          if (item.itemType === 'test' && item.absoluteCategory && item.absoluteCategory._id) {
            categoriesMap[item.absoluteCategory._id] = {
              _id: item.absoluteCategory._id,
              name: item.absoluteCategory.name
            };
          }
          
          // Add all test categories from panels
          if (item.itemType === 'panel' && item.testCategories && item.testCategories.length > 0) {
            item.testCategories.forEach(category => {
              if (category && category._id) {
                categoriesMap[category._id] = {
                  _id: category._id,
                  name: category.name
                };
              }
            });
          }
        });
        
        const uniqueCategories = Object.values(categoriesMap)
          .sort((a, b) => a.name.localeCompare(b.name));
        
        setItems(allItems);
        setFilteredItems(allItems);
        setCategories(uniqueCategories);

        const savedTests = localStorage.getItem('instalabCart');
        if (savedTests) {
          setSelectedTests(allItems.filter(item => savedTests.includes(item._id)));
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setLoading(false);
      }
    };

    const fetchPastTests = async () => {
      const pastOrders= await listOrders({
        select: 'tests panels',
      });

      const pastTestsIds = pastOrders.map(order => order.tests).flat();
      const pastPanelsIds = pastOrders.map(order => order.panels).flat();
      setPastTests([...pastTestsIds, ...pastPanelsIds]);
    }
    

    document.title = 'Build Custom Panel | Instalab';
    fetchData();
    fetchPastTests();
  }, [currentUser]);

  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    const searchFromUrl = searchParams.get('search');

    if (tabFromUrl && tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl);
    }
    
    if (searchFromUrl !== undefined && searchFromUrl !== searchTerm) {
      setSearchTerm(searchFromUrl || '');
    }
  }, [searchParams, activeTab]);

  const searchInItem = useCallback((item, term) => {
    // Create and cache both normalized and original text for each item
    if (!item._searchableText) {
      // Primary fields (name, shortName, longName)
      const primaryFields = [
        item.name,
        item.shortName,
        item.longName
      ].filter(Boolean);

      // Panel tests' primary fields
      const panelTestPrimaryFields = item.tests?.map(test => [
        test.name,
        test.shortName,
        test.longName
      ].filter(Boolean)).flat() || [];

      // Other fields (lowest priority)
      const otherFields = [
        ...(item.altNames || []),
        item.absoluteCategory?.name,
        item.tagline,
        item.productType?.title,
        ...(item.tests?.map(test => [
          ...(test.altNames || []),
          test.absoluteCategory?.name
        ].filter(Boolean)).flat() || [])
      ].filter(Boolean);

      // Store separate searchable text for each tier
      item._primarySearchableText = TextHelper.normalizeText(primaryFields.join(' '));
      item._primaryOriginalText = primaryFields.join(' ').toLowerCase();
      
      item._panelTestsSearchableText = TextHelper.normalizeText(panelTestPrimaryFields.join(' '));
      item._panelTestsOriginalText = panelTestPrimaryFields.join(' ').toLowerCase();

      item._otherSearchableText = TextHelper.normalizeText(otherFields.join(' '));
      item._otherOriginalText = otherFields.join(' ').toLowerCase();
    }

    const normalizedTerm = TextHelper.normalizeText(term);
    const originalTerm = term.toLowerCase();

    // Calculate score
    let score = 0;

    // Tier 1: Primary fields (name, shortName, longName)
    if (item._primaryOriginalText === originalTerm) {
      score += 3000; // Exact match
    } else if (item._primarySearchableText === normalizedTerm) {
      score += 2900;
    } else if (item._primaryOriginalText.startsWith(originalTerm)) {
      score += 2800; // Starts with
    } else if (item._primarySearchableText.startsWith(normalizedTerm)) {
      score += 2700;
    } else if (item._primaryOriginalText.includes(originalTerm)) {
      score += 2600; // Includes
    } else if (item._primarySearchableText.includes(normalizedTerm)) {
      score += 2500;
    }

    // Tier 2: Panel tests' primary fields
    if (score === 0) {
      if (item._panelTestsOriginalText === originalTerm) {
        score += 2000; // Exact match
      } else if (item._panelTestsSearchableText === normalizedTerm) {
        score += 1900;
      } else if (item._panelTestsOriginalText.startsWith(originalTerm)) {
        score += 1800; // Starts with
      } else if (item._panelTestsSearchableText.startsWith(normalizedTerm)) {
        score += 1700;
      } else if (item._panelTestsOriginalText.includes(originalTerm)) {
        score += 1600; // Includes
      } else if (item._panelTestsSearchableText.includes(normalizedTerm)) {
        score += 1500;
      }
    }

    // Tier 3: Other fields
    if (score === 0) {
      if (item._otherOriginalText === originalTerm) {
        score += 1000; // Exact match
      } else if (item._otherSearchableText === normalizedTerm) {
        score += 900;
      } else if (item._otherOriginalText.startsWith(originalTerm)) {
        score += 800; // Starts with
      } else if (item._otherSearchableText.startsWith(normalizedTerm)) {
        score += 700;
      } else if (item._otherOriginalText.includes(originalTerm)) {
        score += 600; // Includes
      } else if (item._otherSearchableText.includes(normalizedTerm)) {
        score += 500;
      }
    }
    
    item.searchScore = score;
    return score > 0;
  }, []);


  const memoizedFilteredItems = useMemo(() => {
    let filtered = items;

    if (searchTerm) {
      filtered = items.filter(item => searchInItem(item, searchTerm));
    }
        
    // Apply filter for popular + recommended tabs
    switch (activeTab) {
      case 'popular':
        filtered = filtered.filter(item => item.isPopular);
        break;
      case 'recommended':
        filtered = filtered.filter(item =>
          (item.itemType === 'panel' && item.code === PanelCode.ADVANCED_FOLLOWUP) 
          || (currentUser?.stats?.recommendedTests?.includes(item._id)) 
          // || (item?.tests?.some(test => currentUser?.stats?.recommendedTests?.includes(test._id)))
        );
        break;
      case 'test-again':
        filtered = filtered.filter(item => pastTests?.includes(item._id)).sort((a, b) => pastTests.indexOf(a.name) - pastTests.indexOf(b.name));
        break;
      default:
        break;
    }
    
    return filtered.sort((a, b) => (b.searchScore || 0) - (a.searchScore || 0));
  }, [items, searchTerm, activeTab, currentUser?.stats?.recommendedTests, pastTests]);

  const letterGroups = useMemo(() => {
    if (searchTerm) {
      return [['Search Results', memoizedFilteredItems]];
    }

    // Use reduce instead of forEach for better performance
    return Object.entries(
      memoizedFilteredItems.reduce((grouped, item) => {
        const name = item.shortName || item.name;
        const firstLetter = name?.charAt(0)?.toUpperCase() || '#';
        
        if (!grouped[firstLetter]) {
          grouped[firstLetter] = [];
        }
        grouped[firstLetter].push(item);
        return grouped;
      }, {})
    ).sort(([a], [b]) => a.localeCompare(b));
  }, [memoizedFilteredItems, searchTerm]);

  const categoryGroups = useMemo(() => {
    const grouped = {};
    
    memoizedFilteredItems.forEach(item => {
      let categoryName = 'Uncategorized';
      let categoryId = 'uncategorized';
      
      if (item.itemType === 'test' && item.absoluteCategory && item.absoluteCategory.name) {
        categoryName = item.absoluteCategory.name;
        categoryId = item.absoluteCategory._id;
        
        if (!grouped[categoryId]) {
          grouped[categoryId] = {
            name: categoryName,
            items: []
          };
        }
        
        grouped[categoryId].items.push(item);
      } else if (item.itemType === 'panel') {
      
        
        // Second pass for panels: add references to additional categories
        // This is only for the "By Category" view when not filtering
        if (!categoryFilter || categoryFilter === 'all') {
          if (item.testCategories && item.testCategories.length > 0) {
            item.testCategories.forEach(category => {
              // Skip the primary category as the panel is already there
              if (item.primaryCategory && category._id === item.primaryCategory._id) {
                return;
              }
              
              if (!grouped[category._id]) {
                grouped[category._id] = {
                  name: category.name,
                  items: []
                };
              }
              
              // Add a reference flag to indicate this is a secondary category
              const panelWithRef = { 
                ...item,
                isSecondaryCategory: true 
              };
              grouped[category._id].items.push(panelWithRef);
            });
          }
        }
      }
    });
    
    return Object.entries(grouped).sort(([, a], [, b]) => 
      a.name?.localeCompare(b.name)
    );
  }, [memoizedFilteredItems]);

  // Create a memoized Set of selected test IDs for faster lookups
  const selectedTestsMap = useMemo(() => {
    return new Set(selectedTests.map(test => test._id));
  }, [selectedTests]);

  // Memoize the expensive isInCartPanel calculation
  const getIsInCartPanel = useCallback((item) => {
    if (item.itemType === 'panel') {
      return selectedTests.find(test => 
        item.tests.every(t => test.tests?.some(t2 => t2._id === t._id))
      );
    }
    return selectedTests.find(test => 
      test.tests?.some(t => t._id === item._id)
    );
  }, [selectedTests]);


  // Effect to restore scroll position after state updates
  useEffect(() => {
    if (scrollPositionRef.current) {
      window.scrollTo(0, scrollPositionRef.current);
    }
  }, [selectedTests, cartOpen]);

  // Effect to maintain scroll position
  useEffect(() => {
    const handleScroll = () => {
      scrollPositionRef.current = window.scrollY;
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Add to cart function
  const addToCart = useCallback((test, showMessage=true) => {
    if (test) {
      if (selectedTests.some(t => t._id === test._id) && showMessage) {
        message.warning(`${test.shortName || test.name} is already in your panel`);
        return;
      }
  
      const updatedSelectedTests = [...selectedTests, test];
      
      // Update localStorage first
      localStorage.setItem('instalabCart', JSON.stringify(updatedSelectedTests.map(t => t._id)));
      
      // Then update state
      setSelectedTests(updatedSelectedTests);

      if (!isMobileView) {
        setCartOpen(true);    
      }

      if (showMessage) {
        message.success(
          {
            content: `Added ${test.shortName || test.name}`,
            icon: <PlusCircleFilled />
          }
        );
      }
    }
  }, [selectedTests, isMobileView]);

  // Remove from cart function
  const removeFromCart = useCallback((test) => {
    if (test) {
      const newSelectedTests = selectedTests.filter(t => t._id !== test._id);
      setSelectedTests(newSelectedTests);

      message.success(
        {
          content: `Removed ${test.shortName || test.name}`,
          icon: <MinusCircleFilled />
        }
      );
      localStorage.setItem('instalabCart', JSON.stringify(newSelectedTests.map(test => test._id)));
    }
  }, [selectedTests]);

  // 1. All Tests Tab View
  const allTestsView = () => {

    const scrollToLetter = (letter, e) => {
      e.preventDefault(); // Prevent default anchor behavior
      const element = document.getElementById(`letter-${letter}`);
      if (element) {
        element.scrollIntoView({ 
          behavior: 'smooth',
          block: 'start'
        });
      }
    };

    // If no items match search, show empty state
    if (letterGroups.length === 0 || (letterGroups.length === 1 && letterGroups[0][0] === 'Search Results' && letterGroups[0][1].length === 0)) {
      return (
        <Empty 
          description="No tests match your search" 
          image={Empty.PRESENTED_IMAGE_SIMPLE} 
        />
      );
    }

    
    return (
      <>
        {!searchTerm && (
          <div className="alphabet-nav">
            <Space wrap>
              {letterGroups.map(([letter]) => (
                letter !== 'Search Results' && (
                  <a
                    key={letter}
                    onClick={(e) => scrollToLetter(letter, e)}
                    className="letter-link"
                    href={`#letter-${letter}`}
                  >
                    <Button type="default">
                      {letter}
                    </Button>
                  </a>
                )
              ))}
            </Space>
          </div>
        )}
        
        <div className="letter-groups">
          {letterGroups.map(([letter, items]) => (
            <div 
              className="letter-group" 
              key={letter} 
              id={`letter-${letter}`}
            >
              {letter !== 'Search Results' &&<Title level={2} className="letter-heading" id={`letter-${letter}`}>
                <span className="letter-badge">
                  {letter}
                </span>
                <span className="letter-count">{items.length} test{items.length === 1 ? '' : 's'}</span>
              </Title>}
              
              <VirtualizedGrid 
              items={items}
              isMobileView={isMobileView}
              renderItem={(item) => (
                <MemoizedTestCard 
                  item={item} 
                  addToPanel={addToCart}
                  removeFromPanel={removeFromCart}
                  isPanel={item.itemType === 'panel'}
                  isInCart={selectedTestsMap.has(item._id)}
                  isInCartPanel={getIsInCartPanel(item)}
                />
              )}
            />
              {letter !== 'Search Results' && <Divider />}
            </div>
          ))}
        </div>
      </>
    );
  };

  // 2. Category Tab View
  const categoryView = () => {

    // If no items match search, show empty state
    if (categoryGroups.length === 0 || categoryGroups.every(([_, category]) => category.items.length === 0)) {
      return (
        <Empty 
          description="No tests match your search" 
          image={Empty.PRESENTED_IMAGE_SIMPLE} 
        />
      );
    }


    return (<div className="category-index">
      {categoryGroups.map(([categoryId, category]) => (
        <div className="category-group" key={categoryId} id={`category-${categoryId}`}>
          <Title level={3} className="category-heading">
            {category.name}
            <span className="category-count">{category.items.length} items</span>
          </Title>
          
          <VirtualizedGrid 
          items={category.items}
          isMobileView={isMobileView}
          renderItem={(item) => (
            <MemoizedTestCard 
              item={item} 
              addToPanel={addToCart}
              removeFromPanel={removeFromCart}
              isPanel={item.itemType === 'panel'}
              isInCart={selectedTestsMap.has(item._id)}
              isInCartPanel={getIsInCartPanel(item)}
            />
          )}
        />
          <Divider />
        </div>
      ))}
    </div>)
  };

  // 3. Popular Tab View
  const popularView = () => (
    <div className="popular-index">
      {memoizedFilteredItems.length > 0 ? (
      <VirtualizedGrid 
        items={memoizedFilteredItems}
        isMobileView={isMobileView}
        renderItem={(item) => (
          <MemoizedTestCard 
            item={item} 
            addToPanel={addToCart}
            removeFromPanel={removeFromCart}
            isPanel={item.itemType === 'panel'}
            isInCart={selectedTestsMap.has(item._id)}
            isInCartPanel={getIsInCartPanel(item)}
          />
        )}
      />
    ) : (
      <Empty 
        description="No popular tests match your search" 
        image={Empty.PRESENTED_IMAGE_SIMPLE} 
      />
    )}

    </div>
  );

  // 4. Recommended Tab View
  const recommendedView = () => (
    <div className="recommended-index">
      {memoizedFilteredItems.length > 0 ? (
        <VirtualizedGrid 
          items={memoizedFilteredItems}
          isMobileView={isMobileView}
          renderItem={(item) => (
            <MemoizedTestCard 
              item={item} 
              addToPanel={addToCart}
              removeFromPanel={removeFromCart}
              isPanel={item.itemType === 'panel'}
              isInCart={selectedTestsMap.has(item._id)}
              isInCartPanel={getIsInCartPanel(item)}
            />
          )}
        />
      ) : (
        <Empty 
          description="No recommended tests match your search" 
          image={Empty.PRESENTED_IMAGE_SIMPLE} 
        />
      )}
    </div>
  );

  // 5. Test Again View
  const testAgainView = () => {
    return (
      <div className="test-again-index">
      {memoizedFilteredItems.length > 0 ? (
        <VirtualizedGrid 
          items={memoizedFilteredItems}
          isMobileView={isMobileView}
          renderItem={(item) => (
            <MemoizedTestCard 
              item={item} 
              addToPanel={addToCart}
              removeFromPanel={removeFromCart}
              isPanel={item.itemType === 'panel'}
              isInCart={selectedTestsMap.has(item._id)}
              isInCartPanel={getIsInCartPanel(item)}
            />
          )}
        />
      ) : (
        <Empty 
          description="No tests match your search" 
          image={Empty.PRESENTED_IMAGE_SIMPLE} 
        />
      )}
    </div>
  );  
  }
  
  // 6. Search Bar
  const FilterBar = () => {
   
    return (<div className="search-filters">
      <div className="filter-controls">
        <SearchInput 
          value={searchTerm}
          setSearchTerm={setSearchTerm}
        />
        
      </div>
      
      {/* Add results count within filter bar */}
      <div className="filter-results-count">
        <Text type="secondary">
          {activeTab === 'popular' 
            ? `${memoizedFilteredItems.length} popular tests match your search`
            : `${memoizedFilteredItems.length} tests match your search`
          }
        </Text>
      </div>
    </div>)
  };

  // Add a check for results in other tabs
  const getResultsInOtherTabs = () => {
    if (activeTab === 'all') return 0;
    
    const allMatches = items.filter(item => {
      return searchInItem(item, searchTerm);
    });
    
    return allMatches.length - filteredItems.length;
  };

  // Handle tab changes with filter reset option
  const handleTabChange = (key) => {

    // Update URL params while preserving search term
    const newParams = new URLSearchParams(searchParams);
    newParams.set('tab', key);
    
    // Always preserve search parameter, even if empty
    newParams.set('search', searchTerm);
    
    setActiveTab(key);
    setSearchParams(newParams);
    
  };

  return (
    <div className="test-index-container">
      <Affix offsetTop={0}>
        <div className="test-index-header">
          <div className="test-index-header-content">
            <div className="test-index-title">
                Build Your Own Panel
                <div className="test-index-description">
                Select from our list of blood tests.
              </div>
            </div>

            <div className="test-index-cart">
              <Cart
                open={cartOpen}
                setOpen={setCartOpen}
                selectedTests={selectedTests}
                removeTest={removeFromCart}
                scrollY={1000}
                />
              </div>
          </div>
        </div>
      </Affix>

      <div className="test-index-content">
      <FilterBar />

      {loading ? (
        <div className="loading-container">
          <Spin size="large" />
          <p>Loading tests ...</p>
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="no-results">
          <Empty 
            description={
              <>
                <p>{activeTab === 'popular' ? 
                  "No popular tests match your search" : 
                  "No tests match your search criteria"}
                </p>
                
                {getResultsInOtherTabs() > 0 && (
                  <div className="other-results-message">
                    <p>Found {getResultsInOtherTabs()} matching tests in other views</p>
                    <Button 
                      type="primary" 
                      onClick={() => setActiveTab('all')}
                    >
                      View all matching tests
                    </Button>
                  </div>
                )}
              </>
            } 
            image={Empty.PRESENTED_IMAGE_SIMPLE} 
          />
        </div>
      ) : (
        <div className="tests-container">
          <Tabs 
            defaultActiveKey="popular"
            activeKey={activeTab}
            onChange={handleTabChange}
            className="view-tabs"
            type="card"
          >
            <Item 
              tab={<span><FileTextOutlined /> All Tests </span>} 
              key="all"
            >
              {allTestsView()}
            </Item>
            {pastTests && pastTests.length > 0 && (
              <Item 
                tab={
                  <Tooltip title="Tests you've ordered before">
                    <span><ReloadOutlined /> Test Again</span>
                  </Tooltip>
                } 
                key="test-again"
              >
                {testAgainView()}
              </Item>
            )}
            {currentUser?.stats?.recommendedTests && currentUser.stats.recommendedTests.length > 0 && (
              <Item 
                tab={
                  <Tooltip title="Tests recommended based on your suboptimal results">
                    <span><StarOutlined /> Recommended</span>
                  </Tooltip>
                } 
                key="recommended"
              >
                {recommendedView()}
              </Item>
            )}

            <Item 
              tab={
                <Tooltip title="Most commonly ordered tests">
                  <span><FireOutlined /> Most Popular</span>
                </Tooltip>
              } 
              key="popular"
            >
              {popularView()}
            </Item>

            <Item 
              tab={<span><AppstoreOutlined /> By Category</span>} 
              key="category"
            >
              {categoryView()}
            </Item>
          </Tabs>
        </div>
      )}

      </div>

      {isMobileView && (
        <BackTop 
          style={{ 
            right: 15,
            bottom: 15
          }}
          visibilityHeight={100}
          duration={500}
        >
          <div className="custom-backtop">
            <UpOutlined />
          </div>
        </BackTop>
      )}
    
    </div>
  )
};